<template>
    <component :is="parentWrapper" class="pa-0">
        <v-container px-3 py-0 fluid grid-list-md :white="isContainerWhite" my-3>
            <v-layout row wrap>
                <v-flex md6 v-if="showLoginForm">
                    <v-card :flat="flatCards" :color="cardColor">
                        <v-card-title primary-title v-if="!isUserLoggedIn">
                            <p class="title mb-0">{{ 'I am already a customer' | translation }}</p>
                        </v-card-title>
                        <template v-if="!isUserLoggedIn">
                            <login-form
                                no-title
                                :input-solo="inputSolo"
                                :input-label-outside="inputLabelOutside"
                                :is-submit-button-round="isNextBtnRound"
                                :login-btn-depressed="nextBtnDepressed"
                                card-class="text-xs-left"
                                :card-color="cardColor"
                            />
                        </template>
                        <template v-else>
                            <v-card-text>
                                <p class="title mb-0">
                                    {{ 'Welcome' | translation }}
                                </p>
                            </v-card-text>
                        </template>
                    </v-card>
                </v-flex>
                <v-flex md6 :class="addressFormClass">
                    <v-card :flat="flatCards" :color="cardColor">
                        <v-card-title primary-title v-if="!isUserLoggedIn">
                            <p class="title mb-0">{{ "I'm new here" | translation }}</p>
                        </v-card-title>
                        <v-card-text>
                            <v-form ref="form" v-model="valid" lazy-validation @submit.prevent>
                                <v-layout row wrap>
                                    <v-flex xs12>
                                        <v-radio-group v-model="isPrivateCustomer">
                                            <v-radio
                                                :label="$language.translate('Ich bin ein Privatkunde')"
                                                :value="true"
                                            ></v-radio>
                                            <v-radio
                                                :label="$language.translate('Ich bin ein Firmenkunde')"
                                                :value="false"
                                            ></v-radio>
                                        </v-radio-group>
                                    </v-flex>
                                    <v-flex xs12>
                                        <component
                                            :is="registrationComponent"
                                            ref="registration"
                                            allow-guest
                                            :input-solo="inputSolo"
                                            :input-label-outside="inputLabelOutside"
                                            :disabled="loading"
                                        />
                                    </v-flex>

                                    <v-flex xs12>
                                        <component
                                            :is="billingAddressComponent"
                                            @addressLoaded="onBillingAddressLoad"
                                            @validateUid="validateUid"
                                            ref="billingAddressForm"
                                            :disabled="loading"
                                            :houseNumberRequired="displayHouseNumberField || houseNumberRequired"
                                            :displayAddressFormProp="displayBillingForm"
                                            :redirect="redirect"
                                            :input-solo="inputSolo"
                                            :input-label-outside="inputLabelOutside"
                                            @submit="onBillingFormSubmit"
                                            :form-class="formClass"
                                            :button-round="isNextBtnRound"
                                            :button-depressed="nextBtnDepressed"
                                            :button-large="buttonLarge"
                                        />
                                    </v-flex>

                                    <v-flex xs12>
                                        <v-checkbox
                                            :disabled="loading"
                                            v-model="otherAddress"
                                            hide-details
                                            v-if="!deactivateShippingAddressSelection"
                                        >
                                            <div slot="label">{{ 'Differing Shipping Address' | translation }}</div>
                                        </v-checkbox>
                                        <component
                                            :is="shippingAddressComponent"
                                            @addressLoaded="onShippingAddressLoad"
                                            ref="shippingAddressForm"
                                            v-if="otherAddress"
                                            :displayList="true"
                                            :disabled="loading"
                                            :input-solo="inputSolo"
                                            :input-label-outside="inputLabelOutside"
                                            :form-class="formClass"
                                            :button-round="isNextBtnRound"
                                            :button-depressed="nextBtnDepressed"
                                            :button-large="buttonLarge"
                                            :houseNumberRequired="displayHouseNumberField || houseNumberRequired"
                                        />
                                    </v-flex>

                                    <birthday-select
                                        class="ma-1 mt-3"
                                        v-if="birthdayField"
                                        v-model="birthday"
                                        :min-age="minAge"
                                        :input-solo="inputSolo"
                                        :input-label-outside="inputLabelOutside"
                                        :disabled="loading"
                                        :required="birthdayFieldRequired"
                                        ref="birthdaySelect"
                                    />

                                    <v-flex xs12 text-xs-right pb-0>
                                        <v-checkbox
                                            class="d-inline-block"
                                            :disabled="loading"
                                            v-model="agb"
                                            :rules="[rules.agb]"
                                            required
                                        >
                                            <div slot="label">
                                                <component
                                                    :is="{
                                                        template:
                                                            '<span>' +
                                                            $language.translate('I agree with the conditions') +
                                                            '</span>',
                                                    }"
                                                ></component>
                                            </div>
                                        </v-checkbox>
                                    </v-flex>

                                    <v-flex xs12 text-xs-right pt-0>
                                        <v-btn
                                            :round="isNextBtnRound"
                                            :color="nextBtnColor"
                                            :disabled="!valid || loading"
                                            @click="submit"
                                            :depressed="nextBtnDepressed"
                                            :large="buttonLarge"
                                        >
                                            {{ 'Continue' | translation }}
                                        </v-btn>
                                    </v-flex>
                                </v-layout>
                            </v-form>
                        </v-card-text>
                    </v-card>
                </v-flex>
            </v-layout>
        </v-container>
    </component>
</template>

<script>
import StepBase from '@breakmedia/prime-vue-stepper/src/StepBase';
import module from './store/addressStep';
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import Vue from 'vue';
import BillingAddressForm from './BillingAddress';
import BirthdaySelect from '@breakmedia/prime-vue-customer/src/BirthdaySelect';
import moment from 'moment';

const moduleName = 'CheckoutAddressStep';
const loginModuleName = 'Login';

if (!Vue.$store.state[moduleName]) {
    Vue.$store.registerModule(moduleName, module);
}

export default {
    //do not set the name - it will break the integration with vuetify stepper
    extends: StepBase,
    components: {
        BirthdaySelect,
    },
    props: {
        route: {
            default: 'address',
        },
        isNextBtnRound: Boolean,
        buttonLarge: Boolean,
        nextBtnDepressed: Boolean,
        nextBtnColor: String,
        inputSolo: Boolean,
        inputLabelOutside: Boolean,
        formClass: String,
        flatCards: Boolean,
        showLoginForm: {
            default: true,
            type: Boolean,
        },
        addressFormClass: {
            type: String,
            default: '',
        },
        cardColor: {
            type: String,
            default: 'white',
        },
        isContainerWhite: Boolean,
        houseNumberRequired: Boolean,
        birthdayField: Boolean,
        birthdayFieldRequired: Boolean,
        minAge: Number,
        deactivateShippingAddressSelection: Boolean,
    },
    data: () => ({
        valid: true,
        rules: {
            required: value => !!value || 'Dieses Feld wird benötigt und darf nicht leer sein.',
            agb: value => !!value || 'Bitte stimmen sie den AGB zu.',
        },
        loading: false,
        showTermsModal: false,
        displayHouseNumberField: false, //used to be re-set from the other step within stepper.
        displayBillingForm: false, //used to be re-set from the other step within stepper.
        redirect: null, //used to be re-set from the other step within stepper.
    }),
    computed: {
        ...mapState('Registration', {
            createAccount: state => state.askPassword,
        }),
        ...mapGetters(loginModuleName, ['isUserLoggedIn']),
        ...mapGetters(BillingAddressForm.name, ['getBirthday']),
        agb: {
            get() {
                return this.getAgb();
            },
            set(value) {
                this.setAgb(value);
            },
        },
        otherAddress: {
            get() {
                return this.getOtherAddress();
            },
            set(value) {
                this.setOtherAddress(value);
            },
        },
        isPrivateCustomer: {
            get() {
                return this.getIsPrivateCustomer();
            },
            set(value) {
                this.setIsPrivateCustomer(value);
            },
        },
        birthday: {
            get() {
                const birthday = this.getBirthday;
                if (birthday) {
                    const dateParts = birthday.substr(0, birthday.indexOf('T')).split('-');
                    return {
                        day: parseInt(dateParts[2]),
                        month: parseInt(dateParts[1]),
                        year: parseInt(dateParts[0]),
                    };
                }

                return {
                    day: null,
                    month: null,
                    year: null,
                };
            },
            set(value) {
                if (value.day && value.month && value.year) {
                    let day = value.day.toString();
                    if (day.length === 1) {
                        day = `0${day}`;
                    }
                    let month = value.month.toString();
                    if (month.length === 1) {
                        month = `0${month}`;
                    }
                    const year = value.year;

                    const date = moment(`${year}-${month}-${day} 00:00 +0000`, 'YYYY-MM-DD HH:mm Z').toDate();
                    this.setBirthday(date);
                }
            },
        },
        registrationComponent() {
            let generalCountry = this.$country.getCountry();
            let billingCountry;

            if (this.$store.state['BillingAddressForm']) {
                billingCountry = this.$store.getters['BillingAddressForm/getCountry'];
            }

            return Vue.options.components['checkout-registration-default'];
        },
        billingAddressComponent() {
            let country = this.$country.getCountry();

            if (Vue.options.components['checkout-billing-address-' + country.toLowerCase()]) {
                return Vue.options.components['checkout-billing-address-' + country.toLowerCase()];
            } else {
                return Vue.options.components['checkout-billing-address-default'];
            }
        },
        shippingAddressComponent() {
            let country = this.$country.getCountry();

            if (Vue.options.components['checkout-shipping-address-' + country.toLowerCase()]) {
                return Vue.options.components['checkout-shipping-address-' + country.toLowerCase()];
            } else {
                return Vue.options.components['checkout-shipping-address-default'];
            }
        },
        deliveryCountry() {
            return this.$order.getDeliveryCountry();
        },
    },
    methods: {
        ...mapGetters(moduleName, ['getAgb', 'getOtherAddress', 'getCreateAccount', 'getIsPrivateCustomer']),
        ...mapMutations(moduleName, ['setAgb', 'setOtherAddress', 'setCreateAccount', 'setIsPrivateCustomer']),
        ...mapMutations(loginModuleName, ['setAuthToken']),
        ...mapMutations(BillingAddressForm.name, ['setBirthday']),
        ...mapActions(moduleName, ['registerCustomer', 'setCustomerDataErrors']),
        ...mapActions('Checkout', ['setBillingAddressId', 'setShippingAddressId']),
        ...mapActions('NotificationModal', ['displayNotificationModal']),
        validate() {
            let result =
                this.$refs.form.validate() && (!this.$refs.birthdaySelect || this.$refs.birthdaySelect.validate());
            if (this.$refs.registration) {
                result = this.$refs.registration.validate() && result;
            }
            result = this.$refs.billingAddressForm.validate() && result;
            result = (!this.otherAddress || this.$refs.shippingAddressForm.validate()) && result;

            return result;
        },
        async submit() {
            this.resetErrors();
            if (this.validate() && (await this.validateUid())) {
                this.loading = true;
                if (!this.isUserLoggedIn) {
                    if (this.createAccount) {
                        this.registerCustomer()
                            .then(result => {
                                if (result.success) {
                                    this.saveAddresses()
                                        .then(() => {
                                            this.loading = false;
                                            this.$emit('onNextStep');
                                        })
                                        .catch(e => {
                                            this.loading = false;
                                            this.showErrorModal();
                                        });
                                } else if (result.errors) {
                                    this.loading = false;
                                    this.setCustomerDataErrors(result.errors);
                                } else {
                                    this.showErrorModal();
                                    this.loading = false;
                                }
                            })
                            .catch(e => {
                                this.showErrorModal();
                                this.loading = false;
                            });
                    } else {
                        this.$session.set('registration-data', {
                            firstName: this.$refs.registration.firstName,
                            lastName: this.$refs.registration.lastName,
                            salutation: this.$refs.registration.salutation,
                            email: this.$refs.registration.email,
                            packingStation: this.$refs.registration.packingStation,
                            packingStationPostCode: this.$refs.registration.packingStationPostCode,
                            fiscalCode: this.$refs.registration.fiscalCode,
                            phone: this.$refs.registration.phone,
                            company: this.$refs.registration.company,
                            uid: this.$refs.registration.uid,
                            askPassword: this.$refs.registration.askPassword,
                        });
                        this.$session.set('customer-address-billing', this.$refs.billingAddressForm.addressFields);
                        if (this.$refs.shippingAddressForm) {
                            this.$session.set(
                                'customer-address-shipping',
                                this.$refs.shippingAddressForm.addressFields,
                            );
                        }
                        this.loading = false;
                        this.$emit('onNextStep');
                    }
                } else {
                    this.saveAddresses()
                        .then(() => {
                            this.loading = false;
                            this.$emit('onNextStep');
                        })
                        .catch(e => {
                            this.loading = false;
                            this.showErrorModal();
                        });
                }
            } else if (this.$refs.billingAddressForm && this.$refs.billingAddressForm.isAddressValid() !== true) {
                this.$refs.billingAddressForm.showAddressForm(true);
            } else if (this.$refs.shippingAddressForm && this.$refs.shippingAddressForm.isAddressValid() !== true) {
                this.$refs.shippingAddressForm.showAddressForm(true);
            }
        },
        saveAddresses() {
            return this.$refs.billingAddressForm.saveAddress().then(result => {
                this.setBillingAddressId(result.id);
                if (this.otherAddress) {
                    return this.$refs.shippingAddressForm.saveAddress().then(result => {
                        this.setShippingAddressId(result.id);
                        this.$refs.shippingAddressForm.loadAddress();
                        return result;
                    });
                }
            });
        },
        resetErrors() {
            if (this.$refs.registration) {
                this.$refs.registration.resetErrors();
            }
        },
        showErrorModal() {
            this.displayNotificationModal({
                message: 'An unexpected problem occurred. Please try again later.',
            });
        },
        onBillingAddressLoad(address) {
            this.setBillingAddressId(address && address.id ? address.id : null);
        },
        onShippingAddressLoad(address) {
            this.setShippingAddressId(address && address.id ? address.id : null);
        },
        onBillingFormSubmit() {
            if (this.isUserLoggedIn) {
                this.saveAddresses().then(() => {
                    this.$emit('goToStep', this.redirect);
                });
            }
        },
        async validateUid() {
            let value = this.$refs.registration.getUid();
            return await this.$refs.registration.validateUid(value);
        },
    },
    watch: {
        active(value) {
            if (!value) {
                this.displayHouseNumberField = false;
                this.displayBillingForm = false;
            }
        },
        deliveryCountry: {
            handler(country) {
                if (country) {
                    this.$country.setCountry(country);
                }
            },
            immediate: true,
        },
    },
};
</script>
